import { APIEndpoints } from '@assembly-web/services';
import { datadogLogs } from '@datadog/browser-logs';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createStore } from 'zustand/vanilla';

import { getFeatureFlagTreatment } from '../config/splitOptions';
import { SplitNames } from '../constants/SplitNames';
import { assemblyAPI } from '../services/assemblyAPI';

export type EntityUserActivity = {
  entityId: string;
  visitCount: number;
  lastVisitedAt: string;
};

export type UrlUserActivity = {
  url: string;
  type: string;
  pageTitle: string;
  visitCount: number;
  lastVisitedAt: string;
  pageDescription: string;
};

export type UserActivity = EntityUserActivity | UrlUserActivity;

type UserActivitiesState = {
  userActivities: UserActivity[];
  clearUserActivities: () => void;
  isTimerActive: boolean;
  startTimer: () => void;
  stopTimer: () => void;
  checkAndSendActivities: () => void;
  userActivityTimer: number | null;
  upsertUserActivity: (userActivity: UserActivity) => void;
};

function isEntityUserActivity(
  activity: UserActivity
): activity is EntityUserActivity {
  return 'entityId' in activity;
}

function isUrlUserActivity(
  activity: UserActivity
): activity is UrlUserActivity {
  return 'url' in activity;
}

const timeOut = 0;
const maxActivities = 1;

async function syncActivitiesToServer(activities: UserActivity[]) {
  const treatment = getFeatureFlagTreatment(
    SplitNames.EnableUserActivitiesTracking
  );
  if (treatment === 'on') {
    await assemblyAPI.post(APIEndpoints.addUserActivity, {
      data: activities,
    });
  }
}

export const userActivitiesStore = createStore<UserActivitiesState>()(
  persist(
    immer((set, get) => ({
      userActivities: [],
      isTimerActive: false,
      userActivityTimer: null,
      clearUserActivities: () => {
        set((state) => {
          state.userActivities = [];
        });
      },
      stopTimer: () => {
        const { isTimerActive, userActivityTimer } = get();
        if (!isTimerActive || !userActivityTimer) {
          return;
        }

        clearInterval(userActivityTimer);
        set((state) => {
          state.isTimerActive = false;
        });
      },
      startTimer: () => {
        const { isTimerActive, checkAndSendActivities } = get();
        if (isTimerActive) {
          return;
        }

        set((state) => {
          state.userActivityTimer = setInterval(
            checkAndSendActivities,
            timeOut
          ) as unknown as number;
          state.isTimerActive = true;
        });
      },
      checkAndSendActivities: async () => {
        const { userActivities, clearUserActivities, stopTimer } = get();
        stopTimer();

        if (userActivities.length > 0) {
          try {
            await syncActivitiesToServer(userActivities);
            clearUserActivities();
          } catch (e) {
            datadogLogs.logger.log('Failed to sync user activities', {
              error: e,
            });
          }
        }
      },
      upsertUserActivity: (userActivity: UserActivity) => {
        set((state) => {
          const userActivities = state.userActivities;

          const upsertActivity = (
            activity: UserActivity,
            key: keyof EntityUserActivity | keyof UrlUserActivity
          ) => {
            const index = userActivities.findIndex((currentActivity) => {
              if (key === 'entityId' && isEntityUserActivity(currentActivity)) {
                const entityUserActivity = activity as EntityUserActivity;
                return (
                  isEntityUserActivity(currentActivity) &&
                  entityUserActivity.entityId === currentActivity.entityId
                );
              }

              if (key === 'url' && isUrlUserActivity(currentActivity)) {
                const urlUserActivity = activity as UrlUserActivity;
                return (
                  isUrlUserActivity(currentActivity) &&
                  urlUserActivity.url === currentActivity.url
                );
              }
            });

            if (index === -1) {
              userActivities.push({
                ...activity,
                visitCount: 1,
                lastVisitedAt: new Date().toISOString(),
              });
            } else {
              userActivities[index] = {
                ...userActivities[index],
                visitCount: userActivities[index].visitCount + 1,
                lastVisitedAt: new Date().toISOString(),
              };
            }
          };

          if (isEntityUserActivity(userActivity)) {
            upsertActivity(userActivity, 'entityId');
          } else if (isUrlUserActivity(userActivity)) {
            upsertActivity(userActivity, 'url');
          }

          state.userActivities = userActivities;

          if (userActivities.length >= maxActivities) {
            get().checkAndSendActivities();
          } else if (userActivities.length > 0) {
            get().startTimer();
          }
        });
      },
    })),
    {
      name: 'userActivities',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
